import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

/**
 * Persistence for list-page UI state (pagination + filters) so it survives a
 * component unmount/remount within the same browser tab.
 *
 * Why this exists: the admin uses flat routes, so opening an edit form unmounts
 * the list page and destroys its local React state. Navigating back via
 * `navigate(-1)` remounts the list fresh, resetting page number and filters to
 * their defaults. Backing this state with sessionStorage lets the list restore
 * exactly where the user left it.
 *
 * Scope is the tab session: state persists across in-app navigation and page
 * refreshes, and clears automatically when the tab is closed. Keys are
 * namespaced per call site (e.g. "posts.page") to avoid cross-page collisions.
 */
const SESSION_PREFIX = "jidt-admin:list:";

const readJson = <T>(key: string, fallback: T): T => {
  try {
    const stored = sessionStorage.getItem(SESSION_PREFIX + key);
    return stored !== null ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(value));
  } catch {
    // Persistence is best-effort; ignore quota / serialization failures.
  }
};

/**
 * Drop-in replacement for `useState` that mirrors the value into sessionStorage.
 * Use for primitives (page number, search text, select filters, sort flags).
 */
export function usePersistedState<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readJson(key, initialValue));
  useEffect(() => {
    writeJson(key, value);
  }, [key, value]);
  return [value, setValue];
}

/**
 * Persisted state for the MUI X (AdapterDayjs) date-range filters.
 *
 * These fields are typed `string | null` across the pages but actually hold
 * `dayjs` objects at runtime (the DatePicker generic infers `string` from the
 * `value` prop). A naive JSON round-trip would corrupt the dayjs object, so we
 * serialize to an ISO string and rehydrate back to a dayjs instance on read —
 * keeping the picker working while staying a drop-in for `useState<string|null>`.
 */
export function usePersistedDateState(
  key: string
): [string | null, Dispatch<SetStateAction<string | null>>] {
  const [value, setValue] = useState<any>(() => {
    const iso = readJson<string | null>(key, null);
    return iso ? dayjs(iso) : null;
  });
  useEffect(() => {
    writeJson(key, value ? dayjs(value).toISOString() : null);
  }, [key, value]);
  return [value, setValue];
}
